import { createContext, useContext, useEffect, useMemo, useState, ReactNode, useCallback } from "react";
import api from "@/services/api";
import { useAuth } from "./AuthContext";


export interface AppNotification {
  id: string;
  type: "info" | "success" | "warning" | "error";
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  link?: string;
}

interface ToastItem {
  id: string;
  type: "info" | "success" | "warning" | "error";
  message: string;
}

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  toasts: ToastItem[];
  addNotification: (n: Omit<AppNotification, "id" | "read" | "createdAt">) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  showToast: (type: ToastItem["type"], message: string) => void;
  dismissToast: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

function mapBackendNotification(n: any): AppNotification {
  return {
    id: String(n.id),
    type: n.type,
    title: n.title,
    message: n.message,
    read: Boolean(n.read),
    createdAt: n.created_at || n.createdAt,
    link: n.link || n.entity_id || undefined,
  };
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [loading, setLoading] = useState(false);

  const { isAuthenticated, isLoading } = useAuth();

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const res = await api.get("/notifications", { params: { unreadOnly: false } });
      const list = (res.data?.data || []).map(mapBackendNotification);
      setNotifications(list);
    } catch {
      // Keep silent: UI can still function without notifications.
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Avoid calling backend while auth is still resolving.
    if (!isAuthenticated) {
      setNotifications([]);
      return;
    }
    if (isLoading) return;
    fetchNotifications();
  }, [fetchNotifications, isAuthenticated, isLoading]);


  const addNotification = useCallback((n: Omit<AppNotification, "id" | "read" | "createdAt">) => {
    const newN: AppNotification = {
      ...n,
      id: `n_${Date.now()}`,
      read: false,
      createdAt: new Date().toISOString(),
    };
    setNotifications((prev) => [newN, ...prev]);
  }, []);

  const markRead = useCallback(
    async (id: string) => {
      // optimistic update
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      try {
        await api.patch(`/notifications/${id}/read`);
      } catch {
        // rollback is intentionally not done to avoid flicker
      }
    },
    []
  );

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await api.patch("/notifications/read-all");
    } catch {
      // ignore
    }
  }, []);

  const showToast = useCallback((type: ToastItem["type"], message: string) => {
    const id = `t_${Date.now()}`;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        toasts,
        addNotification,
        markRead,
        markAllRead,
        showToast,
        dismissToast,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}

